<div class="discount-rejection-coupon-configuration">
  <pdq-card variant="popover" color="white">
    <div class="card-header">
      <pdq-heading element="h5">Configurations</pdq-heading>
    </div>
    <pdq-divider></pdq-divider>

    <div class="card-content">
      <div class="notice-banner">
        <pdq-icon icon="Attention" size="small"></pdq-icon>
        <span
          ><strong>Notice:</strong> The offer only triggers when a customer has entered their email at checkout before
          applying a discount code that was rejected.</span
        >
      </div>

      @if (configurationsFormArray?.length === 0) {
        <p class="empty-state-text body-14-regular">No rejection rules configured yet</p>
      }

      @for (configForm of configurations; track $index; let i = $index) {
        <div class="configuration-item" [formGroup]="configForm">
          <pdq-collapsed>
            <pdq-collapsed-header [arrowLocation]="'left'">
              <div class="rule-header">
                <div class="rule-header-left">
                  <span class="rule-title body-14-medium">{{ configForm.get('ruleTitle')?.value || 'New Rule' }}</span>
                </div>
                <div class="rule-header-right">
                  <pdq-slide-toggle formControlName="active"></pdq-slide-toggle>
                  <pdq-button
                    icon="trash"
                    size="small"
                    variant="ghost"
                    (click)="$event.stopPropagation(); onRemoveConfiguration.emit(i)">
                  </pdq-button>
                  @if (canAddMoreRules) {
                    <img
                      src="assets/icons/copy-to-clipboard.svg"
                      alt="Copy rule group"
                      class="rule-group-header__action-icon"
                      matTooltip="Copy rule group"
                      matTooltipClass="pdq-mat-tooltip"
                      (click)="$event.stopPropagation(); onDuplicateConfiguration.emit(i)" />
                  }
                </div>
              </div>
            </pdq-collapsed-header>

            <pdq-collapsed-content>
              <div class="rule-content">
                <pdq-input label="Internal Rule Title" placeholder="New Rule" formControlName="ruleTitle"></pdq-input>

                <!-- Coupon Rule -->
                <div class="section-group">
                  <span class="section-label body-14-medium">Coupon Rule</span>
                  <pdq-radio-group formControlName="couponRule">
                    @for (option of couponRuleRadioOptions; track option.value) {
                      <pdq-radio-button
                        [value]="option.value"
                        [checked]="configForm.get('couponRule')?.value === option.value"
                        [disabled]="
                          option.value === CouponRule.IS_ALL &&
                          shouldDisableAllCouponCodes &&
                          configForm.get('couponRule')?.value !== CouponRule.IS_ALL
                        "
                        [text]="option.text"></pdq-radio-button>
                    }
                  </pdq-radio-group>

                  @if (
                    configForm.get('couponRule')?.value === CouponRule.INCLUDE ||
                    configForm.get('couponRule')?.value === CouponRule.IS
                  ) {
                    <pdq-input
                      label="Coupon Code"
                      placeholder="EG., summer2024"
                      formControlName="couponCode"
                      [invalid]="configForm.hasError('couponCodeRequired')"
                      [dirty]="false"
                      invalidMsg="Coupon code is required"></pdq-input>
                  }
                </div>
                <rich-text-editor
                  label="Message Title"
                  placeholder="Hey {customer.first_name} {customer.last_name}, use {replacement_discount} for a discount!"
                  [control]="configForm.get('messageTitle')"
                  [config]="dynamicFieldsMentionConfig"
                  [disabledFormats]="allFormatsDisabled"></rich-text-editor>

                <rich-text-editor
                  label="Message Description"
                  placeholder="This is given to you because.."
                  [control]="configForm.get('messageDescription')"
                  [config]="dynamicFieldsMentionConfig"
                  [disabledFormats]="allFormatsDisabled"></rich-text-editor>
                @if (configForm.hasError('messageRequired')) {
                  <span class="validation-error"
                    >*At least one of Message Title or Message Description is required</span
                  >
                }
                <p class="hint">Type &#123; to see dynamic field options.</p>

                <!-- Replacement Discount -->
                <div class="section-group">
                  <span class="section-label body-14-medium">Replacement Discount</span>
                  <pdq-radio-group formControlName="replacementDiscountRule">
                    @for (option of replacementRuleRadioOptions; track $index) {
                      <pdq-radio-button
                        [value]="option.value"
                        [checked]="configForm.get('replacementDiscountRule')?.value === option.value"
                        [text]="option.text"></pdq-radio-button>
                    }
                  </pdq-radio-group>
                  <pdq-checkbox
                    formControlName="autoApply"
                    label="Auto-Apply The Replacement Code"
                    labelSize="small"></pdq-checkbox>

                  @if (configForm.get('replacementDiscountRule')?.value === ReplacementRule.EXISTING_DISCOUNT) {
                    <pdq-dropdown
                      title="Choose discount"
                      formControlName="storeDiscount"
                      [options]="storeDiscountOptions"
                      [placeholder]="configForm.get('storeDiscount')?.value ?? 'Select a discount'"
                      [enableSearch]="true"
                      [externalSearch]="true"
                      (searchChange)="onSearchDiscount.emit($event)"
                      [enableDynamicPosition]="true"
                      [staticListMaxHeight]="'400'"></pdq-dropdown>
                    <p class="hint">Search requires the exact discount code. Partial matches won't return results.</p>
                  }

                  @if (configForm.get('replacementDiscountRule')?.value === ReplacementRule.CREATE_DISCOUNT) {
                    <div class="unique-discount-fields">
                      <div class="discount-row">
                        <pdq-dropdown
                          title="Discount Type"
                          formControlName="couponType"
                          [options]="couponTypeOptions"></pdq-dropdown>
                        <pdq-input-arrows
                          label="Value"
                          [control]="configForm.controls.discountValue"
                          [symbol]="
                            configForm.get('couponType')?.value === CouponType.PERCENTAGE ? '%' : shopCurrencySymbol
                          "
                          [min]="1"
                          [max]="getDiscountValueMax(configForm)"
                          [step]="1"
                          widthSize="fullwidth"></pdq-input-arrows>
                      </div>
                      <pdq-input
                        label="Coupon Expiry Days"
                        type="number"
                        formControlName="couponExpiryDays"
                        [invalid]="
                          configForm.get('couponExpiryDays')?.touched &&
                          configForm.get('couponExpiryDays')?.hasError('required')
                        "
                        invalidMsg="Expiry days is required"></pdq-input>

                      <div class="combines-section">
                        <div class="combines-with-header section-label">
                          <span class="body-14-medium">Combines With</span>
                          <pdq-icon
                            icon="more_information"
                            size="x-large"
                            [matTooltip]="'Allowing combinations lets customers stack this discount with other eligible discounts in their cart.'"
                            matTooltipClass="pdq-mat-tooltip">
                          </pdq-icon>
                        </div>

                        @for (checkbox of combinesWithCheckboxes; track checkbox.controlName) {
                          <pdq-checkbox
                            [formControlName]="checkbox.controlName"
                            [label]="checkbox.label"
                            labelSize="small"></pdq-checkbox>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            </pdq-collapsed-content>
          </pdq-collapsed>
        </div>
      }

      @if (canAddMoreRules) {
        <pdq-button variant="tertiary" (click)="onAddConfiguration.emit()">+ Add Rule Group</pdq-button>
      }
    </div>
  </pdq-card>
</div>
